/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { getShortDateString, calculateEntryAmount } from '@/services/utils';
import { getEntryStartTime, getEntryEndTime, calculateEndTimeFromHours } from '@/utils/dateTimeUtils';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useTasksStore } from '@/services/useTasksStore';
import { storeToRefs } from 'pinia';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { getSupportArticleUrl } from '@/services/supportUrlService';
export const useEntriesStore = defineStore('entriesStore', {
  state: () => {
    const {
      handleError,
      handleSuccess,
      handleInfo
    } = useSnackbarHandler();
    return {
      handleError,
      handleSuccess,
      handleInfo,
      entries: [],
      pagination: {},
      summary: {},
      idM365Enabled: {
        m365: false
      },
      tasksList: [],
      special: [{
        key: 'fax',
        label: 'Fax'
      }, {
        key: 'email',
        label: 'Email'
      }, {
        key: 'telephone',
        label: 'Telephone'
      }, {
        key: 'phone',
        label: 'Telephone'
      }, {
        key: 'preparedraft',
        label: 'Prepare/Draft'
      }],
      loadingExternalEventsList: false,
      externalEventsList: [],
      selectedEventDate: new Date()
    };
  },
  getters: {
    getNewEntries: state => state.entries.filter(entry => entry.isNewEntry === true),
    getExistingEntries: state => state.entries.filter(entry => entry.isNewEntry !== true)
  },
  actions: {
    async listEntries(params) {
      const query = this.buildQuery(params);
      if (params.archived) {
        query.status = params.archived.status;
      }
      const response = await axios({
        method: 'GET',
        url: `/entries`,
        params: query
      });
      let {
        entries,
        pagination,
        summary
      } = response.data;
      pagination = pagination || {};
      summary = summary || {};
      summary.billableHours = summary.billableHours || 0;
      summary.nonBillableHours = summary.nonBillableHours || 0;
      summary.totalAmount = summary.totalAmount || 0;
      entries = entries.map(e => this.formatEntryForDisplay(e));
      const {
        features
      } = storeToRefs(useMeStore());
      if (features.value?.stripDescriptions) {
        await this.includeShortDescriptions();
        // In place: these objects are fresh, and copying each one doubles peak memory on Show All.
        entries.forEach(e => {
          e.shortDescription = this.shortDescription(e);
        });
      }
      this.$patch({
        entries,
        pagination,
        summary
      });
      return {
        entries,
        pagination,
        summary
      };
    },
    async listAllEntriesForExport(params) {
      // Build query using filters but without pagination (limit/offset)
      const query = this.buildQuery(params);

      // Remove pagination parameters to get ALL filtered results
      delete query.limit;
      delete query.offset;
      if (params.archived) {
        query.status = params.archived.status;
      }
      const response = await axios({
        method: 'GET',
        url: `/entries`,
        params: query
      });
      const {
        entries
      } = response.data;
      return entries.map(e => this.formatEntryForDisplay(e));
    },
    buildQuery(params) {
      const query = {
        timeOffset: new Date().getTimezoneOffset()
      };
      if (params.billed) query.billedState = params.billed.id;
      if (params.search) query.search = params.search;
      if (params.account) query.account = params.account.id;
      if (params.limit) query.limit = params.limit;
      if (params.offset) query.offset = params.offset;
      if (params.orderBy) query.orderBy = params.orderBy;
      if (params.order) query.order = params.order;
      if (params.cm?.client) query.clientId = params.cm.client.id;
      if (params.cm?.matter) query.matterId = params.cm.matter.id;
      if (params.charge) query.chargeId = params.charge.id;
      if (params.customFlag) query.customFlag = params.customFlag.id;
      if (params.range?.range?.startDate) query.startDate = params.range.range.startDate.toNetworkDate();
      if (params.range?.range?.endDate) {
        // Add 1 day to end date to make the date range inclusive
        // When user selects "Jan 1 - Jan 5", they expect to see entries from Jan 5 as well
        // Since API filters use "less than" comparison, we need Jan 6 to include Jan 5 entries
        const endDate = new Date(params.range.range.endDate);
        endDate.setDate(endDate.getDate() + 1);
        query.endDate = endDate.toNetworkDate();
      }
      if (params.codes?.activity?.id) query.activity = params.codes.activity.id;
      if (params.codes?.task?.id) query.task = params.codes.task.id;

      // Fixed Fee Assignment filter (maps to backend FixedFeeAssignmentFilter enum)
      // Note: ChargeId (already sent above via params.charge) takes precedence on the backend
      if (params.fixedFeeAssignment?.id && params.fixedFeeAssignment.id !== 'All') {
        query.fixedFeeAssignment = params.fixedFeeAssignment.id;
      }
      return query;
    },
    /**
     * Generates a standardized short description for a time entry.
     * This method analyzes the entry description text and attempts to match it
     * against known task types. It first normalizes the description to make matching
     * more reliable, then searches through tasksList and special terms.
     */
    shortDescription(e) {
      const description = this.normalizeDescription(e.description);
      let task = this.tasksList.find(t => description.indexOf(t.key) >= 0);
      if (!task) {
        task = this.special.find(t => description.indexOf(t.key) >= 0);
      }
      return task ? task.label : e.description;
    },
    normalizeDescription(s) {
      return s.toLowerCase().replace(/[^a-z]/g, '');
    },
    async includeShortDescriptions() {
      const {
        tasksList,
        listTasks
      } = useTasksStore();
      if (this.tasksList.length) {
        return this.tasksList;
      }
      await listTasks();
      this.tasksList = tasksList.map(task => ({
        ...task,
        key: this.normalizeDescription(task.label)
      }));
      this.tasksList.sort((a, b) => a.key.localeCompare(b.key));
      this.$patch({
        tasksList: this.tasksList
      });
    },
    async enableMicrosoft() {
      const url = `${window.leanlaw.domain}/m365/connect?redirect=${encodeURIComponent(window.location.href)}`;
      window.location.href = url;
    },
    async disableM365() {
      const url = `${window.leanlaw.domain}/m365/disconnect?redirect=${encodeURIComponent(window.location.href)}`;
      window.location.href = url;
    },
    async externalEventsStatus() {
      try {
        const response = await axios({
          method: 'GET',
          url: `externalEvents/status`
        });
        this.$patch({
          idM365Enabled: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError('Failed to get M365 external events status:');
      }
    },
    async listExternalEvents() {
      this.loadingExternalEventsList = true;
      try {
        const targetDate = this.selectedEventDate;
        const formattedDate = targetDate.toNetworkDate();
        const response = await axios({
          method: 'GET',
          url: 'externalEvents',
          params: {
            startDate: formattedDate,
            endDate: formattedDate,
            timeOffset: new Date().getTimezoneOffset()
          }
        });
        this.externalEventsList = response.data;
        return response.data;
      } catch (error) {
        // Handle M365 specific errors with multi-line snackbar
        const errorMessage = error.response?.data?.message || error.message || 'Error accessing Microsoft 365 calendar. Please verify your connection and permissions.';
        const multiLineMessage = `${errorMessage}\n\nClick `;

        // Use handleInfo for informational M365 message with primary color
        this.handleInfo(multiLineMessage, {
          multiLine: true,
          timeout: 10000,
          // Longer timeout for multi-line message
          actionText: 'Setup Guide',
          actionCallback: () => {
            const url = getSupportArticleUrl('m365CalendarIntegration');
            window.open(url, '_blank');
          }
        });

        // Clear the events list
        this.externalEventsList = [];
      } finally {
        this.loadingExternalEventsList = false;
      }
    },
    async getUnassignedFixedFeeCount() {
      try {
        const response = await axios({
          method: 'GET',
          url: '/entries/unassigned-fixed-fee-count'
        });
        return response.data?.count ?? 0;
      } catch (error) {
        this.handleError(error);
        return 0;
      }
    },
    async dismissExternalEvent(eventId) {
      try {
        const response = await axios({
          method: 'POST',
          url: `externalEvents/dismiss/${eventId}`
        });

        // Filter out the dismissed event(s) from the local list
        this.externalEventsList = this.externalEventsList.filter(event => event.externalId !== eventId);
        this.handleSuccess('Event dismissed successfully');
        return response.data;
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    },
    recalculateSummary() {
      let summary = {
        totalHours: 0,
        billableHours: 0,
        nonBillableHours: 0,
        fixedFeeHours: 0,
        totalAmount: 0
      };
      let total = 0;
      this.entries.forEach(e => {
        total += 1;
        const hours = Number(e.hoursValue) || 0;
        summary.totalHours += hours;
        const amount = calculateEntryAmount(e.rateAmount, hours);
        switch (e.billingType) {
          case 'billable':
            summary.billableHours += hours;
            summary.totalAmount += amount;
            break;
          case 'nonBillable':
            summary.nonBillableHours += hours;
            break;
          case 'fixedFee':
            summary.fixedFeeHours += hours;
            break;
          default:
            break;
        }
      });
      this.summary = summary;
      this.pagination.total = total;
    },
    setEventsDate(date) {
      this.selectedEventDate = date;
    },
    formatEntryForDisplay(e) {
      // Runs once per entry, so read the store directly: storeToRefs builds a ref for every field of it.
      const features = useMeStore().features;
      const useDateHandling = features?.dateHandling;

      // Story #41530: Extract localDateTime date when feature flag enabled
      const dateValue = useDateHandling && e.localDateTime && e.localDateTime.date ? e.localDateTime.date : e.date;
      const entry = {
        id: e.id,
        date: getShortDateString(dateValue),
        originalDate: dateValue,
        // Preserve original date for export
        timekeeper_initials: e.account?.initials || e.account?.name,
        timekeeper: e.account.name,
        client_Id: e.matter.client.reference,
        billingType: e.billingType,
        client: e.matter.client.label,
        matter_id: e.matter.id,
        matter_reference: e.matter.reference ? e.matter.reference.indexOf('.') > -1 ? `="${e.matter.reference}"` : e.matter.reference : '',
        matter: e.matter.label,
        description: e.description,
        hours: e.hours ? `${e.hours}h` : '-',
        hoursValue: e.hours || 0,
        note: e.note,
        // Story #41530: Use shared utility for start/end time display
        startTime: getEntryStartTime(e, useDateHandling),
        endTime: getEntryEndTime(e, useDateHandling),
        originalStartTime: useDateHandling && e.localDateTime?.time ? e.localDateTime.time : e.startTime,
        // Preserve original for export
        originalEndTime: useDateHandling && e.localDateTime?.time && e.hours != null ? calculateEndTimeFromHours(e.localDateTime.time, e.hours) : e.endTime,
        // Preserve original for export
        rate: e.rate && e.billingType === 'billable' ? e.rate.amount : '-',
        amount: e.rate && e.billingType === 'billable' ? calculateEntryAmount(e.rate.amount, e.hours) : '-',
        rateAmount: e.rate?.amount || 0,
        customFlag: e.customFlag || '',
        billed: e.billed,
        account: e.account,
        codes: e.codes,
        invoice: e.invoice,
        // Keep original invoice object for mark/unmark logic
        charge: e.charge || null,
        // Full charge reference for Fixed Fee column display
        chargeId: e.chargeId || e.charge?.id || null,
        // Track if entry is assigned to a fixed fee charge
        billedState: e.billedState || null,
        // Track billing state (WIP, Interim, etc.) for bulk action eligibility
        actions: true
      };

      // Add shortDescription field if strip descriptions feature is enabled and we have tasks loaded
      if (features?.stripDescriptions && e.description && this.tasksList.length) {
        entry.shortDescription = this.shortDescription(entry);
      }
      return entry;
    }
  }
});